import {survey_config_base} from 'screens/InsomniaSurvey/utils/__survey_config_base';
import {interstitial} from 'screens/InsomniaSurvey/utils/interstitial';
import {dropdownPage, inputPage} from 'screens/InsomniaSurvey/utils/page';
import {dateMaskMMDDYYYY, phoneMaskUS} from 'screens/InsomniaSurvey/utils/inputMasks';
import {RiseSurveyConfig} from 'types';
import {calculatedValues} from './calculatedValues';
import {US_STATES} from './config';
import {onboardingPages} from './onboardingPages';
import {validateBookingEligibility} from './utils';
import InsuranceValidationInterstitial from 'screens/InsomniaSurvey/interstitials/InsuranceValidationInterstitial/InsuranceValidationInterstitial';
import RiseAppHandoffInterstitial from 'screens/InsomniaSurvey/interstitials/RiseAppHandoffInterstitial/RiseAppHandoffInterstitial';
import TimeSelectorInterstitial from 'screens/InsomniaSurvey/interstitials/TimeSelectorInterstitial/TimeSelectorInterstitial';
import CareTeamHandoffInterstitial from 'screens/InsomniaSurvey/interstitials/CareTeamHandoffInterstitial/CareTeamHandoffInterstitial';
import {handleContactInfoSubmission} from 'screens/InsomniaSurvey/utils/accountCreation';
import hero_image_1 from 'surveys/insomnia_funnel/paywall/assets/hero_image_1.png';
import recommended_by from 'surveys/insomnia_funnel/survey/assets/recommended_by.png';

/**
 * Clinic intake that ends in a real appointment booking, served at
 * `/book-appointment/start`.
 *
 * Same onboarding questions as the SleepioRx funnel (`insomnia_funnel`) — both
 * spread `onboardingPages` — but the tail diverges after `insurance_state`:
 * eligible patients pick a session time, create an account, and check out, which
 * schedules the appointment through the eligibility API. The SleepioRx branch is
 * never reachable here (`validateBookingEligibility` pins `sleepio_is_available`
 * to 'no').
 *
 * The distinct `title` matters: survey progress lives under one localStorage key
 * keyed by title, so entering this funnel resets any in-flight `/start` progress
 * and vice versa. See screens/InsomniaSurvey/utils/surveyData.ts.
 */
const SURVEY_TITLE = 'insomnia_booking';

export const BOOKING_BASE_PATH = '/book-appointment';

export const survey_config: RiseSurveyConfig = {
  preloadImages: [hero_image_1, recommended_by],
  ...survey_config_base,
  title: SURVEY_TITLE,
  calculatedValues,
  enableAnswerNewStyle: true,
  onCompleteRedirectTo: () => `${BOOKING_BASE_PATH}/checkout`,

  pages: [
    ...onboardingPages,

    dropdownPage({
      name: 'insurance_state',
      category: 'Sleep Profile',
      title: 'Confirm your state.',
      description: 'Your benefits may include expert sleep help, with most eligible patients paying $0*.',
      choices: US_STATES,
      autoAdvance: false,
      isRequired: true,
      identifyPropertyName: 'state',
      onNext: validateBookingEligibility,
    }),

    // Covered / not-covered result. The validation ran in the previous page's
    // onNext so SurveyJS already knows which of the pages below are visible.
    interstitial({
      name: 'insurance_validation_interstitial',
      category: 'Sleep Profile',
      component: () => <InsuranceValidationInterstitial bookingEnabled />,
    }),

    // Dead end for ineligible state/payer: the not-covered screen takes a waitlist
    // email and jumps here, which hands off to the RISE app.
    interstitial({
      visibleIf: "{insurance_is_valid} == 'no'",
      name: 'rise_app_handoff_interstitial',
      category: 'Sleep Profile',
      component: () => <RiseAppHandoffInterstitial />,
      autoAdvance: true,
    }),

    inputPage({
      visibleIf: "{insurance_is_valid} == 'yes'",
      name: 'insurance_policy',
      category: 'Sleep Profile',
      title: 'Add your {insurance_coverage} policy details',
      description: "We'll verify your insurance details up front so you know what's covered—no surprises.",
      inputs: [
        {
          name: 'member_id',
          placeholder: 'Insurance Member ID',
          isRequired: true,
        },
        {
          name: 'dob',
          placeholder: 'Date of Birth (mm/dd/yyyy)',
          isRequired: true,
          inputMask: dateMaskMMDDYYYY,
        },
      ],
    }),

    // Loads real provider availability for the state/payer and writes
    // appointment_start_time / _end_time / _provider_id, which checkout submits.
    // When nothing is bookable it writes booking_availability: 'none' instead,
    // which routes the patient to the care-team hand-off below.
    interstitial({
      visibleIf: "{insurance_is_valid} == 'yes'",
      name: 'time_selector_interstitial',
      category: 'Sleep Profile',
      component: () => <TimeSelectorInterstitial />,
    }),

    // onNext creates the web account + eligibility token; errors render inline
    // here so the user can retry. Last page when a slot was picked (completing
    // into checkout); when there was no availability the care-team hand-off
    // below follows it and consumes the token this page mints.
    inputPage({
      visibleIf: "{insurance_is_valid} == 'yes'",
      name: 'contact_info',
      category: 'Sleep Profile',
      title: 'Enter your contact info to get your sleep plan with expert session details',
      description: "We'll verify your insurance details up front so you know what's covered—no surprises.",
      inputs: [
        {
          name: 'email',
          placeholder: 'Email',
          isRequired: true,
        },
        {
          name: 'phone',
          placeholder: 'Phone number',
          inputMask: phoneMaskUS,
          isRequired: true,
        },
      ],
      onNext: handleContactInfoSubmission,
    }),

    // Dead end for patients with no bookable slot. Sits after contact_info on
    // purpose: it needs the person_id and eligibility token that
    // handleContactInfoSubmission mints in order to save the patient. Being
    // visible also keeps contact_info from being the last page, so the survey
    // does not complete into a checkout there is no appointment for.
    interstitial({
      visibleIf: "{insurance_is_valid} == 'yes' and {booking_availability} == 'none'",
      name: 'care_team_handoff_interstitial',
      category: 'Sleep Profile',
      component: () => <CareTeamHandoffInterstitial />,
      hideBackButton: true,
    }),
  ],
};

export default survey_config;
